/**
 * Helper utility functions for the SalesGro application
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import dayjs from 'dayjs';

import {
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  MeetingRoom as MeetingRoomIcon,
  Home as HomeIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  Handshake as HandshakeIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Note as NoteIcon,
  Description as DescriptionIcon,
  LocalOffer as LocalOfferIcon,
  Archive as ArchiveIcon,
  Feedback as FeedbackIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeClockIcon,
  Warning as WarningIcon,
  Remove as RemoveIcon,
  TrendingDown as TrendingDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  HorizontalRule as MinusIcon,
  DirectionsCar as DirectionsCarIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
  LaptopMac as LaptopMacIcon,
  DirectionsRun as DirectionsRunIcon,
  BusinessCenter as BusinessCenterIcon,
  Videocam as VideocamIcon
} from '@mui/icons-material';
import { Box } from '@mui/material';
import NoImageTiny from '../components/common/NoImageTiny';
import NoImage from '../components/common/NoImage';
import { Avatar } from '@mui/material';

/**
 * Get color configuration for call status chips
 * @param {string} status - The call status code
 * @returns {object} - Object with background, color, and border color
 */
export const getCallStatusColor = (status) => {
  // Default colors if status is not recognized
  const defaultColors = {
    background: '#e0e0e0',
    color: '#616161',
    borderColor: '#bdbdbd'
  };

  // Call status color mapping
  const statusColors = {
    'ANS': {
      background: '#e8f5e9',
      color: '#2e7d32',
      borderColor: '#a5d6a7'
    },
    'BSY': {
      background: '#fff8e1',
      color: '#ff8f00',
      borderColor: '#ffe082'
    },
    'CAN': {
      background: '#ffebee',
      color: '#c62828',
      borderColor: '#ef9a9a'
    },
    'DEC': {
      background: '#ffebee',
      color: '#c62828',
      borderColor: '#ef9a9a'
    },
    'VM': {
      background: '#e8eaf6',
      color: '#3949ab',
      borderColor: '#9fa8da'
    },
    'MSD': {
      background: '#fce4ec',
      color: '#c2185b',
      borderColor: '#f48fb1'
    },
    'NA': {
      background: '#f5f5f5',
      color: '#757575',
      borderColor: '#e0e0e0'
    },
    'WN': {
      background: '#f3e5f5',
      color: '#7b1fa2',
      borderColor: '#ce93d8'
    },
    'CB': {
      background: '#e1f5fe',
      color: '#0288d1',
      borderColor: '#81d4fa'
    },
    'DNR': {
      background: '#e0f2f1',
      color: '#00796b',
      borderColor: '#80cbc4'
    },
    'OTH': {
      background: '#f1f8e9',
      color: '#558b2f',
      borderColor: '#c5e1a5'
    },
    'NC': {
      background: '#fef2f2',
      color: '#dc2626',
      borderColor: '#fca5a5'
    }
  };

  return statusColors[status] || defaultColors;
};

/**
 * Get color configuration for insight status
 * @param {string} status - The insight status code
 * @returns {object} - Object with background and color
 */
export const getInsightStatusColor = (status) => {
  // Default colors if status is not recognized
  const defaultColors = {
    background: '#f5f5f5',
    color: '#9e9e9e'
  };

  // Insight status color mapping
  const statusColors = {
    'MU': {
      background: '#e3f2fd',
      color: '#1976d2'
    },
    'PVF': {
      background: '#ffebee',
      color: '#c62828'
    },
    'PVS': {
      background: '#e3f2fd',
      color: '#1976d2'
    },
    'PIF': {
      background: '#ffebee',
      color: '#c62828'
    },
    'PIS': {
      background: '#e8f5e9',
      color: '#2e7d32'
    },
    // Special case for when insights are not available
    'no_insights': {
      background: '#eeeeee',
      color: '#757575'
    },
    'pending': {
      background: '#fff8e1',
      color: '#ff8f00'
    },
    'failed': {
      background: '#ffebee',
      color: '#c62828'
    },
    'processing': {
      background: '#e3f2fd',
      color: '#1976d2'
    },
    'processed': {
      background: '#e8f5e9',
      color: '#2e7d32'
    }
  };

  return statusColors[status] || defaultColors;
};

/**
 * Get color for a lead score (1-10)
 * @param {number} score - The lead score between 1-10
 * @returns {object} - Object with background and color
 */
export const getScoreColor = (score) => {
  // Handle null, undefined or NaN
  if (score === null || score === undefined || isNaN(score)) {
    return {
      background: '#f5f5f5',
      color: '#9e9e9e'
    };
  }

  // Normalize score to be between 1-10
  const normalizedScore = Math.max(1, Math.min(100, parseInt(score)));

  // Score ranges
  if (normalizedScore >= 80) {
    return {
      background: '#e8f5e9',
      color: '#2e7d32'
    };
  } else if (normalizedScore >= 50) {
    return {
      background: '#fff8e1',
      color: '#ff8f00'
    };
  } else {
    return {
      background: '#ffebee',
      color: '#c62828'
    };
  }
};

/**
 * Get color configuration for lead status
 * @param {string} status - The lead status
 * @returns {string} - MUI color name
 */
export const getLeadStatusColor = (status) => {
  if (!status) return 'default';
    
  switch (status.toLowerCase()) {
      case 'new':
        return 'info';
      case 'unassigned':
        return 'default';
      case 'assigned':
        return 'primary';
      case 'reassigned':
        return 'secondary';
      case 'contacted':
        return 'info';
      case 'follow_up_scheduled':
        return 'warning';
      case 'ringing':
        return 'secondary';
      case 'qualified':
        return 'success';
      case 'interested':
        return 'success';
      case 'property_shared':
        return 'info';
      case 'site_visit_scheduled':
        return 'warning';
      case 'visited':
        return 'success';
      case 'shortlisted':
        return 'warning';
      case 'offer_made':
        return 'secondary';
      case 'closed_won':
        return 'success';
      case 'lost':
        return 'error';
      case 'not_interested':
        return 'error';
      case 'cold_lead':
        return 'default';
      case 'unreachable':
        return 'error';
      case 'no_response':
        return 'error';
      case 'no_show':
        return 'error';
      case 'not_looking_currently':
        return 'default';
      default:
        return 'default';
  };
};

/**
 * Get color configuration for lead stage
 * @param {string} stage - The lead stage
 * @returns {object} - Object with background and color
 */
export const getLeadStageColor = (stage) => {
  // Default colors if stage is not recognized
  const defaultColors = {
    background: '#f5f5f5',
    color: '#9e9e9e',
    borderColor: '#e0e0e0'
  };

  // Lead stage color mapping with distinct colors
  const stageColors = {
    'new': {
      background: '#e3f2fd',
      color: '#1565c0',
      borderColor: '#90caf9'
    },
    'contacted': {
      background: '#f3e5f5',
      color: '#7b1fa2',
      borderColor: '#ce93d8'
    },
    'qualified': {
      background: '#e0f2f1',
      color: '#00796b',
      borderColor: '#80cbc4'
    },
    'follow_up': {
      background: '#fff8e1',
      color: '#ff8f00',
      borderColor: '#ffe082'
    },
    'site_visit': {
      background: '#ede7f6', 
      color: '#4527a0',
      borderColor: '#b39ddb'
    },
    'negotiation': {
      background: '#fff3e0',
      color: '#ef6c00',
      borderColor: '#ffe0b2'
    },
    'closure': {
      background: '#e8f5e9',
      color: '#2e7d32',
      borderColor: '#a5d6a7'
    }
  };

  return stageColors[stage] || defaultColors;
};

/**
 * Format phone number with country code
 * @param {string} countryCode - Country code (e.g., "+91")
 * @param {string} phoneNumber - Phone number without country code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (countryCode, phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Format based on country code
  switch (countryCode) {
    case '+1': // US/Canada
      if (digits.length === 10) {
        return `${countryCode} (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      }
      break;
    case '+91': // India
      if (digits.length === 10) {
        return `${countryCode} ${digits.substring(0, 5)} ${digits.substring(5)}`;
      }
      break;
    // Add more country-specific formatting as needed
    default:
      // Generic formatting for other country codes
      return `${countryCode} ${digits}`;
  }
  
  // Default fallback if specific formatting not applied
  return `${countryCode} ${digits}`;
};

/**
 * Format currency with appropriate symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined) return '—';
  
  const currencySymbols = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currency] || '';
  
  return `${symbol}${parseFloat(amount).toLocaleString('en-IN')}`;
};

/**
 * Format date as short en-IN (e.g. 06 Mar 2025)
 * @param {string|Date} dateValue - ISO date string or Date object
 * @returns {string} - Formatted date or '—' if falsy/invalid
 */
export const formatDate = (dateValue) => {
  if (!dateValue) return '—';
  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Format currency with no decimal places (e.g. ₹1,234)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} - Formatted currency string
 */
export const formatCurrencyNoDecimals = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined) return '—';
  const currencySymbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
  const symbol = currencySymbols[currency] || '';
  return `${symbol}${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
};

/**
 * Format currency with human-readable labels (e.g., 10Cr, 5L)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} - Formatted currency string with human-readable label
 */
export const formatCurrencyWithLabel = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined) return '—';

  const numAmount = parseFloat(amount);
  const formatted = formatCurrency(numAmount, currency);

  let label = '';
  if (numAmount >= 10000000) {
    const val = parseFloat((numAmount / 10000000).toFixed(2));
    const isExact = (numAmount / 10000000) % 1 === 0;
    label = `${isExact ? '' : '~'}${val}Cr`;
  } else if (numAmount >= 100000) {
    const val = parseFloat((numAmount / 100000).toFixed(2));
    const isExact = (numAmount / 100000) % 1 === 0;
    label = `${isExact ? '' : '~'}${val}L`;
  } else if (numAmount >= 1000) {
    const val = parseFloat((numAmount / 1000).toFixed(2));
    const isExact = (numAmount / 1000) % 1 === 0;
    label = `${isExact ? '' : '~'}${val}K`;
  }

  return label ? `${formatted} (${label})` : formatted;
};

/**
 * Get human-readable date/time from ISO string or Date object
 * @param {string|Date} dateTime - ISO date string or Date object
 * @param {string} format - Format type ('date', 'time', 'datetime', 'relative')
 * @returns {string} - Formatted date/time string
 */
export const formatDateTime = (dateTime, format = 'datetime') => {
  if (!dateTime) return '';
  
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const options = {
    date: { day: 'numeric', month: 'short', year: 'numeric' },
    time: { hour: 'numeric', minute: 'numeric', hour12: true },
    datetime: { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: 'numeric', hour12: true
    }
  };
  
  if (format === 'relative') {
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    
    // Fall back to date format for older dates
    return date.toLocaleDateString('en-US', options.date);
  }
  
  return date.toLocaleString('en-US', options[format] || options.datetime);
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  
  return text.length <= maxLength
    ? text
    : `${text.substring(0, maxLength)}...`;
};

/**
 * Get initials from name (up to 2 characters)
 * @param {string} name - Full name
 * @returns {string} - Initials (up to 2 chars)
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  const parts = name.split(' ').filter(Boolean);
  
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Convert camelCase or snake_case to Title Case with spaces
 * @param {string} str - String to convert
 * @returns {string} - Converted string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  
  // Handle snake_case
  if (str.includes('_')) {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle camelCase
  return str
    // Insert a space before all caps and uppercase the first character
    .replace(/([A-Z])/g, ' $1')
    // Upper case first character of the string
    .replace(/^./, function(str) { return str.toUpperCase(); })
    .trim();
};


export const getStatusColor = (status) => {
  if (!status) return 'default';
  
  switch (status.toLowerCase()) {
    case 'new':
    case 'unassigned':
    case 'assigned':
    case 'reassigned':
      return 'info';
    case 'contacted':
    case 'follow_up_scheduled':
    case 'ringing':
      return 'primary';
    case 'qualified':
    case 'interested':
    case 'property_shared':
    case 'site_visit_scheduled':
    case 'visited':
      return 'success';
    case 'shortlisted':
    case 'offer_made':
      return 'warning';
    case 'closed_won':
      return 'success';
    case 'lost':
    case 'not_interested':
    case 'cold_lead':
    case 'unreachable':
    case 'no_response':
    case 'no_show':
    case 'not_looking_currently':
      return 'error';
    default:
      return 'default';
  }
};

// Common styles for Autocomplete
export const autocompleteStyles = {
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
    minHeight: '32px',
    fontWeight: 400
  },
  '& .MuiInputBase-input': {
    fontSize: '0.75rem',
    fontWeight: 400
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)'
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '2px'
  },
  '& .MuiAutocomplete-clearIndicator': {
    padding: '2px'
  }
};


  // Helper function to get labels from default options
  export const getLabelsFromDefaults = (savedValues, defaultOptions) => {
    if (!savedValues || !savedValues.length) return [];
    
    // Create a map of type to label from default options
    const optionsMap = defaultOptions.reduce((map, option) => {
      map[option.type] = option.label;
      return map;
    }, {});
    
    // Map saved values to their labels or use the value itself if no label is found
    return savedValues.map(value => {
      // If value is already a string, try to find its label or use the value
      if (typeof value === 'string') {
        return optionsMap[value] || value;
      }
      // If value is an object with type, use that to find label
      if (value.type) {
        return optionsMap[value.type] || value.type;
      }
      // If value is an object with label, use that
      if (value.label) {
        return value.label;
      }
      // Fallback to a string representation
      return JSON.stringify(value);
    });
  };


  // Get status color
  // Get status color with better combination
export const getBookingStatusColor = (status) => {
  console.log('status', status);
    const bookingStatusColors = {
      draft: 'secondary.light',
      pending: 'warning',
      reserved: 'info',
      confirmed: 'primary', // Confirmed, progressing
      completed: 'success', // Successfully closed
      cancelled: 'error',   // Dropped out / negative
      expired: 'secondary', // Expired / inactive
    };
  
    return bookingStatusColors[status] || 'default';
  };



  export const getBookingStatusProps = (status, status_verbose) => {
    const status_color = getBookingStatusColor(status);
    switch ((status || '').toLowerCase()) {
      case 'draft':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'pending':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'reserved':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'confirmed':
        return { icon: <CheckCircleIcon />, color: status_color, label: status_verbose };
      case 'completed':
          return { icon: <CheckCircleIcon />, color: status_color, label: status_verbose };
      case 'no_show':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'cancelled':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      case 'rescheduled':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'postponed':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      default:
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
    }
  };

  // Payment status → MUI color scheme
  export const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'secondary'; // Not yet invoiced
      case 'pending':
        return 'warning'; // Awaiting payment
      case 'partially_paid':
        return 'info'; // Partial progress
      case 'paid':
      case 'completed':
        return 'success'; // Payment done
      case 'refunded':
        return 'error'; // Refunded fully
      case 'failed':
        return 'error'; // Failed transaction
      case 'partially_refunded':
        return 'warning'; // Partial reversal
      default:
        return 'default'; // fallback
    }
  };



  export const getPaymentStatusProps = (status, status_verbose) => {
    const status_color = getPaymentStatusColor(status);
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'partial':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'completed':
        return { icon: <CheckCircleIcon />, color: status_color, label: status_verbose };
      case 'failed':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      case 'refunded':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      case 'partially_refunded':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      default:
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
    }
  };



  export const generateFormatedLink = (link, text) => {
    return (
      <MuiLink
        to={link}
        component={RouterLink}
        sx={{
          textDecoration: 'none',
          color: 'text.primary',
          '&:hover': {
            color: 'success.main',
            textDecoration: 'underline'
          }
        }}
      >
        {text}
      </MuiLink>
    );
  };

  export const getLostDealReasonColor = (reason) => {
    switch (reason) {
      case 'Price':
        return 'error';
      case 'Location':
        return 'warning';
      case 'Competition':
        return 'secondary';
      case 'Timing':
        return 'info';
      case 'Other':
        return 'default';   
    }
  };

/**
 * Format currency in Indian format with lakhs (L) and crores (Cr)
 * @param {number|string} value - Amount to format
 * @returns {string} - Formatted amount like "50L" or "1.5Cr"
 */
export const formatIndianCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'Any';
  
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return 'Any';
  
  // Format based on value
  if (numValue >= 10000000) {
    // If >= 1 crore, show as Cr
    return `${(numValue / 10000000).toFixed(1)}Cr`;
  } else if (numValue >= 100000) {
    // Show as lakhs
    return `${(numValue / 100000).toFixed(0)}L`;
  } else if (numValue >= 1000) {
    // Show as thousands
    return `${(numValue / 1000).toFixed(0)}K`;
  }
  
  return `₹${numValue}`;
};

/**
 * Check if a value is already a formatted currency string (e.g. "₹9.31L", "16.74L")
 */
const isPreFormattedCurrency = (v) => {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  return s.length > 0 && (/^₹?[\d.]+\s*(L|Cr|K)?$/i.test(s) || s.includes('L') || s.includes('Cr') || s.includes('₹'));
};

/**
 * Format budget range in Indian format.
 * Accepts raw numbers (min/max or min_value/max_value) or pre-formatted strings (e.g. "₹9.31L", "₹16.74L").
 * @param {object} budget - Budget object with min/max or min_value/max_value (numbers or strings)
 * @returns {string} - Formatted budget like "50L-1.5Cr" or "₹9.31L-₹16.74L" or "Not specified"
 */
export const formatBudgetRange = (budget) => {
  if (!budget || typeof budget !== 'object') return 'Not specified';

  const min = budget.min ?? budget.min_value;
  const max = budget.max ?? budget.max_value;

  const minIsNumber = min != null && min !== '' && !Number.isNaN(Number(min));
  const maxIsNumber = max != null && max !== '' && !Number.isNaN(Number(max));
  const minIsFormatted = isPreFormattedCurrency(min);
  const maxIsFormatted = isPreFormattedCurrency(max);

  const hasMin = minIsNumber || minIsFormatted;
  const hasMax = maxIsNumber || maxIsFormatted;

  if (!hasMin && !hasMax) return 'Not specified';

  const formattedMin = hasMin
    ? (minIsFormatted ? String(min).trim() : formatIndianCurrency(min))
    : null;
  const formattedMax = hasMax
    ? (maxIsFormatted ? String(max).trim() : formatIndianCurrency(max))
    : null;

  if (formattedMin === 'Any' && formattedMax === 'Any') return 'Not specified';
  if (hasMin && hasMax) return `${formattedMin}-${formattedMax}`;
  return hasMin ? String(formattedMin) : String(formattedMax);
};

export const getInvoiceStatusColor = (status) => {
  console.log('status', status);
  const statusColors = {
    'UP': 'error', // Unpaid
    'PD': 'success', // Paid
    'PP': 'warning', // Partially Paid
    'OV': 'error', // Overdue
    'RP': 'info', // Refunded
    'CN': 'default' // Cancelled
  };
  return statusColors[status] || 'default';
};

export const getPaymentModeIcon = (mode) => {
  const modeIcons = {
    'CR': '💳', // Credit Card
    'DB': '💳', // Debit Card
    'CS': '💰', // Cash
    'BT': '🏦' // Bank Transfer
  };
  return modeIcons[mode] || '💳';
};

// Activity Management Helpers

/**
 * Get activity configuration based on activity type
 * @param {string} activityType - The activity type from backend
 * @returns {object} - Object with icon, color, background, and label
 */
export const getActivityConfig = (activityType) => {
  const configs = {
    // Lead Creation & Management
    'lead_created': {
      icon: <PersonAddIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Lead Created'
    },
    'lead_note_added': {
      icon: <NoteIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Note Added'
    },
    'lead_contact_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Contact Updated'
    },
    'lead_deleted': {
      icon: <DeleteIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Lead Deleted'
    },

    // Status & Stage Changes
    'status_changed': {
      icon: <SettingsIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Status Changed'
    },
    'stage_changed': {
      icon: <TrendingUpIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Stage Changed'
    },

    // Assignment
    'assigned': {
      icon: <AssignmentIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Lead Assigned'
    },
    'reassigned': {
      icon: <AssignmentIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Lead Reassigned'
    },

    // Communication
    'call_made': {
      icon: <PhoneIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Call Made'
    },
    'email_sent': {
      icon: <EmailIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Email Sent'
    },
    'message_sent': {
      icon: <ChatIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Message Sent'
    },
    'meeting_scheduled': {
      icon: <MeetingRoomIcon fontSize="small" />,
      color: '#7b1fa2',
      background: '#f3e5f5',
      label: 'Meeting Scheduled'
    },
    'meeting_completed': {
      icon: <CheckCircleIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Meeting Completed'
    },

    // Property Related
    'property_shared': {
      icon: <HomeIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Property Shared'
    },
    'property_shortlisted': {
      icon: <ThumbUpIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Property Shortlisted'
    },
    'site_visit_scheduled': {
      icon: <LocationOnIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Site Visit Scheduled'
    },
    'site_visit_completed': {
      icon: <CheckCircleIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Site Visit Completed'
    },
    'site_visit_cancelled': {
      icon: <CancelIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Site Visit Cancelled'
    },

    // Follow-ups
    'followup_scheduled': {
      icon: <ScheduleIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Follow-up Scheduled'
    },
    'followup_completed': {
      icon: <CheckCircleIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Follow-up Completed'
    },
    'followup_missed': {
      icon: <CancelIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Follow-up Missed'
    },

    // Requirements & Preferences
    'requirements_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Requirements Updated'
    },
    'preferences_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Preferences Updated'
    },
    'additional_preferences_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Additional Preferences Updated'
    },
    'property_preferences_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Property Preferences Updated'
    },
    'budget_updated': {
      icon: <CurrencyRupeeIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Budget Updated'
    },

    // Negotiations & Closure
    'price_quoted': {
      icon: <CurrencyRupeeIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Price Quoted'
    },
    'offer_made': {
      icon: <HandshakeIcon fontSize="small" />,
      color: '#7b1fa2',
      background: '#f3e5f5',
      label: 'Offer Made'
    },
    'offer_accepted': {
      icon: <ThumbUpIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Offer Accepted'
    },
    'offer_rejected': {
      icon: <ThumbDownIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Offer Rejected'
    },
    'deal_closed': {
      icon: <CheckCircleIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Deal Closed'
    },

    // Lead Disqualification
    'lead_lost': {
      icon: <ThumbDownIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Lead Lost'
    },
    'lead_dropped': {
      icon: <CancelIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Lead Dropped'
    },
    'marked_junk': {
      icon: <DeleteIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Marked as Junk'
    },

    // Notes & Documents
    'note_added': {
      icon: <NoteIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Note Added'
    },
    'note_updated': {
      icon: <EditIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Note Updated'
    },
    'note_deleted': {
      icon: <DeleteIcon fontSize="small" />,
      color: '#d32f2f',
      background: '#ffebee',
      label: 'Note Deleted'
    },
    'document_uploaded': {
      icon: <DescriptionIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Document Uploaded'
    },
    'document_shared': {
      icon: <DescriptionIcon fontSize="small" />,
      color: '#1976d2',
      background: '#e3f2fd',
      label: 'Document Shared'
    },
    'tags_added': {
      icon: <LocalOfferIcon fontSize="small" />,
      color: '#7b1fa2',
      background: '#f3e5f5',
      label: 'Tags Added'
    },

    // Additional Activities
    'lead_reactivated': {
      icon: <TrendingUpIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Lead Reactivated'
    },
    'lead_archived': {
      icon: <ArchiveIcon fontSize="small" />,
      color: '#757575',
      background: '#f5f5f5',
      label: 'Lead Archived'
    },
    'feedback_received': {
      icon: <FeedbackIcon fontSize="small" />,
      color: '#ed6c02',
      background: '#fff4e5',
      label: 'Feedback Received'
    },
    'contract_signed': {
      icon: <DescriptionIcon fontSize="small" />,
      color: '#2e7d32',
      background: '#e8f5e9',
      label: 'Contract Signed'
    }
  };

  return configs[activityType] || {
    icon: <AssignmentIcon fontSize="small" />,
    color: '#757575',
    background: '#f5f5f5',
    label: 'Activity'
  };
};

/**
 * Get activity background color for striped approach
 * @param {number} index - Index of the activity item
 * @param {string} baseColor - Base color for the activity
 * @returns {string} - Background color with opacity
 */
export const getActivityBackground = (index, baseColor) => {
  const isEven = index % 2 === 0;
  return isEven ? `${baseColor}15` : 'transparent'; // 15 = 9% opacity
};

/**
 * Get activity border color based on activity type
 * @param {string} activityType - The activity type
 * @returns {string} - Border color
 */
export const getActivityBorderColor = (activityType) => {
  const config = getActivityConfig(activityType);
  return `${config.color}30`; // 30 = 18% opacity
};



export const getStageColor = (stage) => {
  if (!stage) return 'default';
  
  // Define colors for all lead stages based on the pipeline
  switch (stage.toLowerCase()) {
    case 'new':
      return 'info'; // Light blue for new leads
    case 'contacted':
      return 'primary'; // Blue for contacted leads
    case 'qualified':
      return 'warning'; // Orange for qualified leads
    case 'site_visit':
      return 'secondary'; // Purple for site visit stage
    case 'negotiation':
      return 'warning'; // Orange for negotiation
    case 'closure':
      return 'success'; // Green for closure
    case 'junk':
      return 'error'; // Red for junk leads
    case 'archived':
      return 'default'; // Grey for archived leads
    default:
      return 'default';
  }
};

export const getTruncatedTagDisplay = (tags, limit = 3) => {
  if (!tags || !tags.length) return [];
  
  // Map preferences to display strings
  const displayValues = tags.map(tag => {
    if (typeof tag === 'string') return tag;
    return tag.label || tag.key || JSON.stringify(tag);
  });
  
  // Return either all or up to the limit
  return limit ? displayValues.slice(0, limit) : displayValues;
};


  // Get item type icon based on item_type
export const getTaskItemTypeIcon = (itemType) => {
    switch (itemType) {
      case 'contact_call':
      case 'lead_followup':
        return <PhoneIcon sx={{ fontSize: '0.75rem', color: '#3B82F6' }} />;
      case 'site_visit':
        return <HomeIcon sx={{ fontSize: '0.75rem', color: '#8B5CF6' }} />;
      case 'task':
      case 'general_task':
        return <DescriptionIcon sx={{ fontSize: '0.75rem', color: '#F59E0B' }} />;
      case 'meeting':
        return <CalendarIcon sx={{ fontSize: '0.75rem', color: '#10B981' }} />;
      default:
        return <AccessTimeIcon sx={{ fontSize: '0.75rem', color: '#64748B' }} />;
    }
  };


  // Get item type color based on item_type
export const getTaskItemTypeColor = (itemType) => {
    switch (itemType) {
      case 'initial_contact':
        return { border: '#10B981', background: '#D1FAE5' }; // Green
      case 'lead_followup':
        return { border: '#3B82F6', background: '#DBEAFE' }; // Blue
      case 'site_visit':
        return { border: '#8B5CF6', background: '#E9D5FF' }; // Purple
      case 'document_collection':
        return { border: '#F59E0B', background: '#FED7AA' }; // Orange
      case 'general_task':
        return { border: '#6B7280', background: '#F3F4F6' }; // Gray
      case 'other':
        return { border: '#EF4444', background: '#FEE2E2' }; // Red
      default:
        return { border: '#6B7280', background: '#F3F4F6' }; // Default gray
    }
  };

  // Get priority color for left border
export const getTaskPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return { border: '#EF4444', background: '#FEF2F2' }; // Red
      case 'medium':
        return { border: '#F59E0B', background: '#FFFBEB' }; // Orange
      case 'low':
        return { border: '#10B981', background: '#ECFDF5' }; // Green
      default:
        return { border: '#6B7280', background: '#F9FAFB' }; // Default gray
    }
  };

  // Get priority icon (Jira style)
export const getTaskPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'highest':
      case 'urgent':
        return <ArrowUpIcon sx={{ fontSize: '0.75rem', color: '#EF4444' }} />; // red
      case 'high':
        return <ArrowUpIcon sx={{ fontSize: '0.75rem', color: '#F97316' }} />; // orange
      case 'medium':
        return <MinusIcon sx={{ fontSize: '0.75rem', color: '#3B82F6' }} />; // blue
      case 'low':
        return <ArrowDownIcon sx={{ fontSize: '0.75rem', color: '#10B981' }} />; // green
      case 'lowest':
        return <ArrowDownIcon sx={{ fontSize: '0.75rem', color: '#059669' }} />; // dark green
      default:
        return <MinusIcon sx={{ fontSize: '0.75rem', color: '#6B7280' }} />; // gray
    }
  };


  export const getTaskStatusColor = (status) => {
    const priorityColorMap = {
      high: '#F87171',   // red-400
      medium: '#FBBF24', // yellow-400
      low: '#34D399',    // green-400
      pending: '#FB923C', // orange-400
      in_progress: '#38BDF8', // blue-400
      completed: '#4ADE80', // green-400
      cancelled: '#FB7185', // red-400
      on_hold: '#E879F9', // purple-400
    };
  
    const bgColorMap = {
      high: '#FEF2F2',    // red-50
      medium: '#FFFBEB',  // yellow-50
      low: '#ECFDF5',     // green-50
      pending: '#FFF7ED', // Light orange
      in_progress: '#F0F9FF', // Light blue
      completed: '#F0FDF4', // Light green
      cancelled: '#FEF2F2', // Light red
      on_hold: '#FDF4FF', // Light purple
    };
  

    switch (status?.toLowerCase()) {
      case 'pending':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'in_progress':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'completed':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'cancelled':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      case 'on_hold':
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
      default:
        return {
          background: bgColorMap[status?.toLowerCase()],
          border: priorityColorMap[status?.toLowerCase()]
        };
    }
  };


  // Get status color
  export const getSiteVisitStatusColor = (status) => {
    const defaultColors = {
      background: '#f5f5f5',
      color: '#757575',
      borderColor: '#e0e0e0'
    };
    switch (status) {
      case 'scheduled':
        return {
          background: '#e3f2fd',
          color: '#1976d2',
          borderColor: '#bbdefb'
        }; // Blue
      case 'confirmed':
        return {
          background: '#e8f5e8',
          color: '#2e7d32',
          borderColor: '#c8e6c9'
        }; // Green
      case 'completed':
        return {
          background: '#e8f5e8',
          color: '#2e7d32',
          borderColor: '#c8e6c9'
        }; // Green
      case 'no_show':
        return {
          background: '#ffebee',
          color: '#d32f2f',
          borderColor: '#ffcdd2'
        }; // Red
      case 'cancelled':
        return {
          background: '#ffebee',
          color: '#d32f2f',
          borderColor: '#ffcdd2'
        }; // Red
      case 'rescheduled':
        return {
          background: '#fff3e0',
          color: '#f57c00',
          borderColor: '#ffcc02'
        }; // Orange
      case 'postponed':
        return {
          background: '#fff3e0',
          color: '#f57c00',
          borderColor: '#ffcc02'
        }; // Orange
      case 'pending':
        return {
          background: '#f3e5f5',
          color: '#7b1fa2',
          borderColor: '#e1bee7'
        }; // Purple
      case 'upcoming':
        return {
          background: '#e1f5fe',
          color: '#0277bd',
          borderColor: '#b3e5fc'
        }; // Light Blue
      default:
        return defaultColors; // Default grey
    }
  };

  export const getSiteVisitTravelTypeColor = (travelType) => { 
    switch (travelType) {
      case 'self':
        return {
          icon: <DirectionsCarIcon sx={{ fontSize: '0.9rem', color: '#2563EB' }} />, // Blue Car
          color: '#2563EB',
          background: '#DBEAFE'
        };
      case 'company':
        return {
          icon: <BusinessCenterIcon sx={{ fontSize: '0.9rem', color: '#16A34A' }} />, // Green briefcase = company-arranged
          color: '#16A34A',
          background: '#DCFCE7'
        };
      case 'agent':
        return {
          icon: <PersonIcon sx={{ fontSize: '0.9rem', color: '#9333EA' }} />, // Purple agent icon
          color: '#9333EA',
          background: '#EDE9FE'
        };
      case 'cab':
        return {
          icon: <LocalTaxiIcon sx={{ fontSize: '0.9rem', color: '#F59E0B' }} />, // Yellow cab
          color: '#F59E0B',
          background: '#FEF9C3'
        };
      case 'virtual':
        return {
          icon: <VideocamIcon sx={{ fontSize: '0.9rem', color: '#EA580C' }} />, // Orange camera
          color: '#EA580C',
          background: '#FFEDD5'
        };
      default:
        return {
          icon: <DirectionsRunIcon sx={{ fontSize: '0.9rem', color: '#64748B' }} />, // Gray walking
          color: '#64748B',
          background: '#F3F4F6'
        };
    }
  };


  export const getProjectStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'available':
        return {
          icon: <CheckCircleIcon />,
          color: 'success',
          background: '#F0FDF4',
          borderColor: '#22C55E',
          label: 'Available'
        };
      case 'inactive':
      case 'unavailable':
        return {
          icon: <CancelIcon />,
          color: 'error',
          background: '#FEF2F2',
          borderColor: '#EF4444',
          label: 'Unavailable'
        };
      case 'upcoming':
      case 'coming_soon':
        return {
          icon: <WarningIcon />,
          color: 'warning',
          background: '#FFFBEB',
          borderColor: '#F59E0B',
          label: 'Coming Soon'
        };
      case 'sold':
        return {
          icon: <CheckCircleIcon />,
          color: 'info',
          background: '#EFF6FF',
          borderColor: '#3B82F6',
          label: 'Sold'
        };
      case 'under_negotiation':
      case 'reserved':
        return {
          icon: <WarningIcon />,
          color: 'secondary',
          background: '#F3F4F6',
          borderColor: '#6B7280',
          label: status?.toLowerCase() === 'under_negotiation' ? 'Under Negotiation' : 'Reserved'
        };
      case 'under_construction':
        return {
          icon: <ScheduleIcon />,
          color: 'info',
          background: '#F0F9FF',
          borderColor: '#0EA5E9',
          label: 'Under Construction'
        };
      default:
        return {
          icon: <CheckCircleIcon />,
          color: 'default',
          background: '#F9FAFB',
          borderColor: '#9CA3AF',
          label: 'Active'
        };
    }
  };


  // Render project thumbnail
  export const renderThumbnail = (thumbnail, flag='tiny') => {
      if (thumbnail) {
        if(flag == 'medium') {
          return (
            <Avatar
              src={thumbnail}
              variant="rounded"
              sx={{ width: '80%', height: '80%' }}
            />
          );
        } else if(flag == 'small') {
          return (
            <Avatar
              src={thumbnail}
              variant="rounded"
              sx={{ width: 60, height: 60 }}
            />
          );
        } else {
          return (
            <Avatar
              src={thumbnail}
              variant="rounded"
              sx={{ width:  40, height: 40 }}
            />
          );
        }
      }
      if (flag === 'tiny') {
        return (
          <NoImageTiny
            width={60}
            height={40}
            borderRadius="4px"
            iconSize={16}
            textSize="caption"
            text="No Preview"
          />
        );
      } 
      return (
        <NoImage 
          textSize="caption"
          text="No Preview"
        />
      );
    };
  

    export const getIndianCities = () => {
      const INDIAN_CITIES = [
      // Maharashtra
      { city: "Mumbai", state: "Maharashtra"},
      { city: "Pune", state: "Maharashtra"},
      { city: "Nagpur", state: "Maharashtra"},
      { city: "Thane", state: "Maharashtra"},
      { city: "Nashik", state: "Maharashtra"},
      { city: "Aurangabad", state: "Maharashtra"},
      { city: "Solapur", state: "Maharashtra"},
      { city: "Kolhapur", state: "Maharashtra"},
      { city: "Amravati", state: "Maharashtra"},
      { city: "Nanded", state: "Maharashtra"},
      
      // Delhi
      { city: "New Delhi", state: "Delhi"},
      { city: "Delhi", state: "Delhi"},
      
      // Karnataka
      { city: "Bangalore", state: "Karnataka"},
      { city: "Mysore", state: "Karnataka"},
      { city: "Hubli", state: "Karnataka"},
      { city: "Mangalore", state: "Karnataka"},
      { city: "Belgaum", state: "Karnataka"},
      { city: "Gulbarga", state: "Karnataka"},
      
      // Telangana
      { city: "Hyderabad", state: "Telangana"},
      { city: "Warangal", state: "Telangana"},
      { city: "Karimnagar", state: "Telangana"},
      { city: "Nizamabad", state: "Telangana"},
      
      // Tamil Nadu
      { city: "Chennai", state: "Tamil Nadu"},
      { city: "Coimbatore", state: "Tamil Nadu"},
      { city: "Madurai", state: "Tamil Nadu"},
      { city: "Salem", state: "Tamil Nadu"},
      { city: "Tiruchirappalli", state: "Tamil Nadu"},
      { city: "Vellore", state: "Tamil Nadu"},
      
      // West Bengal
      { city: "Kolkata", state: "West Bengal"},
      { city: "Howrah", state: "West Bengal"},
      { city: "Durgapur", state: "West Bengal"},
      
      // Gujarat
      { city: "Ahmedabad", state: "Gujarat"},
      { city: "Surat", state: "Gujarat"},
      { city: "Vadodara", state: "Gujarat"},
      { city: "Rajkot", state: "Gujarat"},
      { city: "Bhavnagar", state: "Gujarat"},
      { city: "Jamnagar", state: "Gujarat"},
      
      // Uttar Pradesh
      { city: "Lucknow", state: "Uttar Pradesh"},
      { city: "Kanpur", state: "Uttar Pradesh"},
      { city: "Varanasi", state: "Uttar Pradesh"},
      { city: "Agra", state: "Uttar Pradesh"},
      { city: "Prayagraj", state: "Uttar Pradesh"},
      { city: "Ghaziabad", state: "Uttar Pradesh"},
      { city: "Noida", state: "Uttar Pradesh"},
      
      // Rajasthan
      { city: "Jaipur", state: "Rajasthan"},
      { city: "Jodhpur", state: "Rajasthan"},
      { city: "Kota", state: "Rajasthan"},
      { city: "Bikaner", state: "Rajasthan"},
      { city: "Ajmer", state: "Rajasthan"},
      { city: "Udaipur", state: "Rajasthan"},
      
      // Andhra Pradesh
      { city: "Visakhapatnam", state: "Andhra Pradesh"},
      { city: "Vijayawada", state: "Andhra Pradesh"},
      { city: "Guntur", state: "Andhra Pradesh"},
      { city: "Nellore", state: "Andhra Pradesh"},
      { city: "Kurnool", state: "Andhra Pradesh"},
      
      // Kerala
      { city: "Thiruvananthapuram", state: "Kerala"},
      { city: "Kochi", state: "Kerala"},
      { city: "Kozhikode", state: "Kerala"},
      { city: "Thrissur", state: "Kerala"},
      { city: "Kollam", state: "Kerala"},
      
      // Madhya Pradesh
      { city: "Bhopal", state: "Madhya Pradesh"},
      { city: "Indore", state: "Madhya Pradesh"},
      { city: "Jabalpur", state: "Madhya Pradesh"},
      { city: "Gwalior", state: "Madhya Pradesh"},
      { city: "Ujjain", state: "Madhya Pradesh"},
      
      // Punjab
      { city: "Chandigarh", state: "Punjab"},
      { city: "Ludhiana", state: "Punjab"},
      { city: "Amritsar", state: "Punjab"},
      { city: "Jalandhar", state: "Punjab"},
      { city: "Patiala", state: "Punjab"},
      
      // Haryana
      { city: "Gurgaon", state: "Haryana"},
      { city: "Faridabad", state: "Haryana"},
      { city: "Panipat", state: "Haryana"},
      { city: "Yamunanagar", state: "Haryana"},
      
      // Bihar
      { city: "Patna", state: "Bihar"},
      { city: "Gaya", state: "Bihar"},
      { city: "Bhagalpur", state: "Bihar"},
      { city: "Muzaffarpur", state: "Bihar"},
      
      // Odisha
      { city: "Bhubaneswar", state: "Odisha"},
      { city: "Cuttack", state: "Odisha"},
      { city: "Rourkela", state: "Odisha"},
      { city: "Brahmapur", state: "Odisha"},
      
      // Assam
      { city: "Guwahati", state: "Assam"},
      { city: "Silchar", state: "Assam"},
      { city: "Dibrugarh", state: "Assam"},
      
      // Jharkhand
      { city: "Ranchi", state: "Jharkhand"},
      { city: "Jamshedpur", state: "Jharkhand"},
      { city: "Dhanbad", state: "Jharkhand"},
      { city: "Bokaro", state: "Jharkhand"},
      
      // Chhattisgarh
      { city: "Raipur", state: "Chhattisgarh"},
      { city: "Bhilai", state: "Chhattisgarh"},
      { city: "Bilaspur", state: "Chhattisgarh"},
      
      // Uttarakhand
      { city: "Dehradun", state: "Uttarakhand"},
      { city: "Haridwar", state: "Uttarakhand"},
      { city: "Roorkee", state: "Uttarakhand"},
      
      // Himachal Pradesh
      { city: "Shimla", state: "Himachal Pradesh"},
      { city: "Solan", state: "Himachal Pradesh"},
      { city: "Mandi", state: "Himachal Pradesh"},
      
      // Goa
      { city: "Panaji", state: "Goa"},
      { city: "Margao", state: "Goa"},
      { city: "Vasco da Gama", state: "Goa"},
      
      // Tripura
      { city: "Agartala", state: "Tripura"},
      
      // Manipur
      { city: "Imphal", state: "Manipur"},
      
      // Meghalaya
      { city: "Shillong", state: "Meghalaya"},
      
      // Nagaland
      { city: "Kohima", state: "Nagaland"},
      
      // Mizoram
      { city: "Aizawl", state: "Mizoram"},
      
      // Arunachal Pradesh
      { city: "Itanagar", state: "Arunachal Pradesh"},
      
      // Sikkim
      { city: "Gangtok", state: "Sikkim"},
      
      // Union Territories
      { city: "Port Blair", state: "Andaman and Nicobar Islands"},
      { city: "Kavaratti", state: "Lakshadweep"},
      { city: "Silvassa", state: "Dadra and Nagar Haveli and Daman and Diu"}
    ];
    return INDIAN_CITIES;

    }
  

// Convert number to words in Indian format
export const numberToWords = (num) => {
  if (num === 0) return 'zero';
  
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  
  const convertLessThanOneThousand = (n) => {
    if (n === 0) return '';
    
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    }
    if (n < 1000) {
      return ones[Math.floor(n / 1000)] + ' hundred' + (n % 100 !== 0 ? ' ' + convertLessThanOneThousand(n % 100) : '');
    }
  };
  
  const convert = (n) => {
    if (n === 0) return 'zero';
    
    const crore = Math.floor(n / 10000000);
    const lakh = Math.floor((n % 10000000) / 100000);
    const thousand = Math.floor((n % 100000) / 1000);
    const remainder = n % 1000;
    
    let result = '';
    
    if (crore > 0) {
      result += convertLessThanOneThousand(crore) + ' crore';
    }
    
    if (lakh > 0) {
      if (result) result += ' ';
      result += convertLessThanOneThousand(lakh) + ' lakh';
    }
    
    if (thousand > 0) {
      if (result) result += ' ';
      result += convertLessThanOneThousand(thousand) + ' thousand';
    }
    
    if (remainder > 0) {
      if (result) result += ' ';
      result += convertLessThanOneThousand(remainder);
    }
    
    return result;
  };
  
  return convert(num);
};



export const defaultDateRange = {
  filter: { id: 'All', label: 'All Time' },
  currentStart: dayjs().startOf('day'),
  currentEnd: dayjs().endOf('day'),
  compareStart: null,
  compareEnd: null,
};

export const NavigationPolicyFromDashboard = ({ cardType, dateRange, additionalFilters }) => {
  const basePolicy = {
    all_results: false,
    date_range: {
      start_date: dateRange.currentStart.format('YYYY-MM-DD'),
      end_date: dateRange.currentEnd.format('YYYY-MM-DD')
    }
  };

  const policyMap = {
    new_leads: {
      page: '/leads',
      filters: { lead_stage: ['new'] }
    },
    unassigned_leads: {
      page: '/leads',
      filters: { lead_stage: ['new'], status: ['unassigned'] }
    },
    untouched_leads: {
      page: '/leads',
      filters: { lead_stage: ['new', 'contacted', 'qualified', 'negotiation'], not_contacted: true }
    },
    no_reminder_leads: {
      page: '/leads',
      filters: { followup_missing: true }
    },
    details_shared: {
      page: '/leads',
      filters: { lead_stage: ['qualified'] }
    },
    site_visit_scheduled: {
      page: '/site-visits',
      filters: { status: ['scheduled'] }
    },
    site_visit_confirmed: {
      page: '/site-visits',
      filters: { status: ['confirmed'] }
    },
    site_visit_done: {
      page: '/site-visits',
      filters: { status: ['completed'] }
    },
    site_visits: {
      page: '/site-visits',
      filters: {}
    },
    total_leads: {
      page: '/leads',
      filters: {}
    },
    not_interested_leads: {
      page: '/leads',
      filters: { lead_stage: ['not_interested'] }
    },
    leads_at_risk: {
      page: '/leads',
      filters: { risk: 'no_activity_7d' }
    },
    dormant_leads: {
      page: '/leads',
      filters: { dormant: true }
    },
    units_sold: {
      page: '/bookings',
      filters: {}
    },
    sales_revenue: {
      page: '/bookings',
      filters: {}
    },
    commission_revenue: {
      page: '/bookings',
      filters: {}
    },
    commission_received: {
      page: '/bookings',
      filters: {}
    },
    overdue_commission: {
      page: '/bookings',
      filters: {}
    },
    lead_conversion_rate: {
      page: '/leads',
      filters: {}
    },
    revenue_per_unit_sold: {
      page: '/bookings',
      filters: {}
    }
  };

  const selectedPolicy = policyMap[cardType] || {};
  return {
    ...basePolicy,
    ...selectedPolicy,
    filters: { ...(selectedPolicy.filters || {}), ...(additionalFilters || {}) }
  };
};

/** Material-style palette shared by avatar + tag helpers */
export const AVATAR_COLOR_PALETTE = [
  '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
  '#FFC107', '#FF9800', '#FF5722', '#795548', '#9E9E9E', '#607D8B', '#E91E63', '#5C6BC0', '#26A69A',
];

const _paletteIndex = (seed) => {
  const s = String(seed ?? '');
  if (!s) return 0;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % AVATAR_COLOR_PALETTE.length;
};

/** Stable “random” color per agent / id (same seed → same color). */
export const getAvatarColorFromString = (seed) => AVATAR_COLOR_PALETTE[_paletteIndex(seed)];

export const getRandomAvatarColor = () => {
  return AVATAR_COLOR_PALETTE[Math.floor(Math.random() * AVATAR_COLOR_PALETTE.length)];
};

/**
 * Distinct chip colors for topic / tag labels (tinted bg, solid label, soft border).
 * @param {string} label
 * @returns {{ bgcolor: string, color: string, borderColor: string }}
 */
export const getTopicTagChipColors = (label) => {
  const hex = getAvatarColorFromString(label);
  return {
    bgcolor: `${hex}1F`,
    color: hex,
    borderColor: `${hex}4D`,
  };
};

export const getPriceHelperTextWords = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const num = Number(value);
  if (Number.isNaN(num) || num <= 0) return '';

  const units = [
    '', 'one', 'two', 'three', 'four', 'five',
    'six', 'seven', 'eight', 'nine'
  ];

  const teens = [
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen',
    'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'
  ];

  const tens = [
    '', '', 'twenty', 'thirty', 'forty',
    'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
  ];

  const twoDigit = (n) => {
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
  };

  let remaining = Math.floor(num);
  const parts = [];

  const crore = Math.floor(remaining / 10000000);
  if (crore) {
    parts.push(`${twoDigit(crore)} crore`);
    remaining %= 10000000;
  }

  const lakh = Math.floor(remaining / 100000);
  if (lakh) {
    parts.push(`${twoDigit(lakh)} lakh`);
    remaining %= 100000;
  }

  const thousand = Math.floor(remaining / 1000);
  if (thousand) {
    parts.push(`${twoDigit(thousand)} thousand`);
  }

  return toTitleCase(parts.join(' '));
};

const formatIndianShort = (value) => {
  if (value == null) return '';

  if (value >= 10000000) {
    const cr = value / 10000000;
    return `${cr % 1 === 0 ? cr : cr.toFixed(1)} Cr`;
  }

  if (value >= 100000) {
    const l = value / 100000;
    return `${l % 1 === 0 ? l : l.toFixed(1)} L`;
  }

  return value.toLocaleString('en-IN');
};

const getPriceRangeLabel = ([min, max], sliderMax) => {
  if (min == null && max == null) {
    return `₹ 0 to ₹ ${formatIndianShort(sliderMax)}`;
  }

  if (min != null && max == null) {
    return `₹ ${formatIndianShort(min)}+`;
  }

  if (min == null && max != null) {
    return `Up to ₹ ${formatIndianShort(max)}`;
  }

  return `₹ ${formatIndianShort(min)} to ₹ ${formatIndianShort(max)}`;
};

export default {
  getCallStatusColor,
  getInsightStatusColor,
  getScoreColor,
  getLeadStatusColor,
  getLeadStageColor,
  formatPhoneNumber,
  formatCurrency,
  formatCurrencyWithLabel,
  formatDateTime,
  truncateText,
  getInitials,
  toTitleCase,
  getStatusColor,
  getStageColor,
  autocompleteStyles,
  getLabelsFromDefaults,
  getBookingStatusColor,
  getPaymentStatusColor,
  generateFormatedLink,
  getBookingStatusProps,
  getPaymentStatusProps,
  getLostDealReasonColor,
  getTaskStatusColor,
  getActivityConfig,
  getActivityBackground,
  getActivityBorderColor,
  getTruncatedTagDisplay,
  getTaskItemTypeIcon,
  getTaskItemTypeColor,
  getTaskPriorityColor,
  getTaskPriorityIcon,
  getSiteVisitStatusColor,
  getSiteVisitTravelTypeColor,
  getProjectStatusConfig,
  renderThumbnail,
  getIndianCities,
  numberToWords,
  defaultDateRange,
  NavigationPolicyFromDashboard,
  getRandomAvatarColor,
  getAvatarColorFromString,
  getTopicTagChipColors,
  AVATAR_COLOR_PALETTE,
  getPriceHelperTextWords,
  getPriceRangeLabel
};